import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Settings, Building2, Hash, Mail, MapPin, Save, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';

export const AdminSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    company_name: '',
    company_address: '',
    company_email: '',
    registration_number: '',
    vat_enabled: false,
    vat_number: '',
    pricing_monthly_gbp: 4.99,
    pricing_yearly_gbp: 49.00,
    pricing_family_gbp: 7.99,
    pricing_fleet_gbp: 49.99,
    currency: 'gbp'
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;

      const response = await fetch(`${backendUrl}/api/admin/settings`, {
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Failed to fetch settings');

      const data = await response.json();
      setSettings(data);
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;

      const response = await fetch(`${backendUrl}/api/admin/settings`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(settings)
      });

      if (!response.ok) throw new Error('Failed to update settings');

      toast.success('Settings updated successfully');
    } catch (error) {
      console.error('Update error:', error);
      toast.error('Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6 max-w-4xl">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6 pb-6 max-w-4xl"
    >
      {/* Header */}
      <div>
        <h1 className="text-4xl font-semibold tracking-tight flex items-center gap-3">
          <Settings className="h-8 w-8" />
          Admin Settings
        </h1>
        <p className="text-muted-foreground mt-1">Manage business information and configuration</p>
      </div>

      {/* Business Information */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <Card className="card-elevated">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              <CardTitle>Business Information</CardTitle>
            </div>
            <CardDescription>Company details for invoices and legal documents</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="company_name">Company Name</Label>
                <Input
                  id="company_name"
                  value={settings.company_name}
                  onChange={(e) => setSettings({ ...settings, company_name: e.target.value })}
                  placeholder="VehicleBuddy Ltd"
                  data-testid="admin-company-name"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="company_email">Company Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-5 w-5 text-muted-foreground" />
                  <Input
                    id="company_email"
                    type="email"
                    value={settings.company_email}
                    onChange={(e) => setSettings({ ...settings, company_email: e.target.value })}
                    placeholder="hello@vehiclebuddy.co.uk"
                    className="pl-10"
                    data-testid="admin-company-email"
                  />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="company_address">Company Address</Label>
              <div className="relative">
                <MapPin className="absolute left-3 top-3 h-5 w-5 text-muted-foreground" />
                <Input
                  id="company_address"
                  value={settings.company_address || ''}
                  onChange={(e) => setSettings({ ...settings, company_address: e.target.value })}
                  placeholder="123 High Street, London, UK"
                  className="pl-10"
                  data-testid="admin-company-address"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="registration_number">Company Registration Number</Label>
              <div className="relative">
                <Hash className="absolute left-3 top-3 h-5 w-5 text-muted-foreground" />
                <Input
                  id="registration_number"
                  value={settings.registration_number || ''}
                  onChange={(e) => setSettings({ ...settings, registration_number: e.target.value })}
                  placeholder="12345678"
                  className="pl-10"
                  data-testid="admin-registration-number"
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* VAT Settings */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <Card className="card-elevated">
          <CardHeader>
            <CardTitle>VAT Configuration</CardTitle>
            <CardDescription>Value Added Tax settings for invoicing</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="vat_enabled">VAT Registered</Label>
                <p className="text-sm text-muted-foreground">
                  Enable if your business is registered for VAT
                </p>
              </div>
              <Switch
                id="vat_enabled"
                checked={settings.vat_enabled}
                onCheckedChange={(checked) => setSettings({ ...settings, vat_enabled: checked })}
                data-testid="admin-vat-enabled"
              />
            </div>

            {settings.vat_enabled && (
              <div className="space-y-2">
                <Label htmlFor="vat_number">VAT Number</Label>
                <div className="relative">
                  <Hash className="absolute left-3 top-3 h-5 w-5 text-muted-foreground" />
                  <Input
                    id="vat_number"
                    value={settings.vat_number || ''}
                    onChange={(e) => setSettings({ ...settings, vat_number: e.target.value })}
                    placeholder="GB123456789"
                    className="pl-10"
                    data-testid="admin-vat-number"
                  />
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>

      {/* Pricing Configuration */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        <Card className="card-elevated">
          <CardHeader>
            <CardTitle>Pricing Configuration</CardTitle>
            <CardDescription>Display pricing for all subscription tiers (GBP)</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="pricing_monthly_gbp">Premium Monthly (£)</Label>
                <Input
                  id="pricing_monthly_gbp"
                  type="number"
                  step="0.01"
                  value={settings.pricing_monthly_gbp}
                  onChange={(e) => setSettings({ ...settings, pricing_monthly_gbp: parseFloat(e.target.value) })}
                  data-testid="admin-pricing-premium-monthly"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="pricing_yearly_gbp">Premium Yearly (£)</Label>
                <Input
                  id="pricing_yearly_gbp"
                  type="number"
                  step="0.01"
                  value={settings.pricing_yearly_gbp}
                  onChange={(e) => setSettings({ ...settings, pricing_yearly_gbp: parseFloat(e.target.value) })}
                  data-testid="admin-pricing-premium-yearly"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="pricing_family_gbp">Family Monthly (£)</Label>
                <Input
                  id="pricing_family_gbp"
                  type="number"
                  step="0.01"
                  value={settings.pricing_family_gbp}
                  onChange={(e) => setSettings({ ...settings, pricing_family_gbp: parseFloat(e.target.value) })}
                  data-testid="admin-pricing-family"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="pricing_fleet_gbp">Fleet Monthly (£)</Label>
                <Input
                  id="pricing_fleet_gbp"
                  type="number"
                  step="0.01"
                  value={settings.pricing_fleet_gbp}
                  onChange={(e) => setSettings({ ...settings, pricing_fleet_gbp: parseFloat(e.target.value) })}
                  data-testid="admin-pricing-fleet"
                />
              </div>
            </div>

            <p className="text-xs text-muted-foreground">
              Note: Changing these values only affects the display. Stripe pricing must be updated separately.
            </p>
          </CardContent>
        </Card>
      </motion.div>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          disabled={saving}
          size="lg"
          className="gap-2"
          data-testid="admin-save-button"
        >
          {saving ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="h-5 w-5" />
              Save Settings
            </>
          )}
        </Button>
      </div>
    </motion.div>
  );
};

export default AdminSettings;
